// The word that an offer you made was answered, lapsed, or that war began —
// and the word that tells you whether your own answer took.
//
// The sim only publishes the table as it stands, so this compares each
// snapshot with the one before (`treatyNews`) and holds the result a few
// seconds. A missed snapshot at worst loses a notice; the table stays right.
//
// Your own answer is the other half. The sim publishes no event for it, and
// the intent's own result never reaches the player: an answer the sim would
// not take used to be a click that did nothing at all. The answer is
// remembered here and read back off the table — if the offer left and the
// relation is what it promised, the answer took; if the offer left and
// nothing changed, it did not.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TreatyActions } from '../sim/useSimWorld/useTreatyIntents.js';
import {
  type AnsweredOffer,
  type Diplomacy,
  type TreatyNews,
  answerNews,
  offerFrom,
  treatyNews,
} from './diplomacy.js';
import { type Dynasty, marriageNews } from './dynasty.js';

const NOTICE_MS = 8000;

export interface Notice extends TreatyNews {
  readonly id: number;
}

/** The treaty lane: the notices on it, and the one thing you do on it. */
export interface TreatyDesk {
  /** The notices, oldest first. */
  readonly notices: readonly Notice[];
  /**
   * Answer the offer waiting on you from `other`. False when the sim would
   * not take the answer — the offer was no longer on the table — which the
   * lane then says out loud.
   */
  readonly answer: (other: number, accept: boolean) => boolean;
}

export function useTreatyNews(
  diplomacy: Diplomacy | null,
  dynasty: Dynasty | null,
  tick: number,
  me: number,
  treaty: TreatyActions,
): TreatyDesk {
  const prev = useRef<Diplomacy | null>(null);
  const prevDynasty = useRef<Dynasty | null>(null);
  const pending = useRef(new Map<number, AnsweredOffer>());
  const nextId = useRef(1);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [notices, setNotices] = useState<readonly Notice[]>([]);

  // Each notice owns its timer: a new snapshot every tick must not reset them.
  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const t of live) clearTimeout(t);
      live.clear();
    };
  }, []);

  const push = useCallback((news: readonly TreatyNews[]) => {
    if (news.length === 0) return;
    const fresh = news.map((n) => ({ ...n, id: nextId.current++ }));
    setNotices((all) => [...all, ...fresh]);
    const ids = new Set(fresh.map((n) => n.id));
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setNotices((all) => all.filter((n) => !ids.has(n.id)));
    }, NOTICE_MS);
    timers.current.add(timer);
  }, []);

  useEffect(() => {
    const news = [
      ...treatyNews(prev.current, diplomacy, me, tick),
      ...marriageNews(prevDynasty.current, dynasty, me, tick),
    ];
    // The offers you answered: the table says whether the answer took.
    const answered = [...pending.current.values()];
    const outcome = answerNews(diplomacy, me, answered);
    for (const a of answered) {
      const still = diplomacy?.proposals.some((p) => p.from === a.other && p.to === me) ?? true;
      if (!still) pending.current.delete(a.other);
    }
    prev.current = diplomacy;
    prevDynasty.current = dynasty;
    push([...news, ...outcome]);
  }, [diplomacy, dynasty, tick, me, push]);

  const answer = (other: number, accept: boolean): boolean => {
    const offer = offerFrom(diplomacy, me, other);
    const ok = accept ? treaty.accept(other) : treaty.decline(other);
    if (!ok) {
      // The sim would not take the answer: the offer had already left the
      // table. Say so — a click that does nothing is a bug the player feels
      // and cannot see.
      push([{ kind: 'stale', other, state: offer?.state ?? 'neutral' }]);
      return false;
    }
    if (offer) {
      pending.current.set(other, {
        other,
        state: offer.state,
        accept,
        expiresTick: offer.expiresTick,
      });
    }
    return true;
  };

  return { notices, answer };
}
